/**
 * Presentation helpers for the Expense Tracking module + integer-paise
 * money utilities used cross-module (Timesheet billing rates, payroll,
 * Hiring CTC etc.).
 *
 * MONEY (Hard Req #5 / migration-011 convention): an amount is INR
 * *paise* as an integer. JS binary floats cannot represent decimal
 * currency exactly, so we NEVER do float arithmetic on money. The
 * rupee/paise split is pure integer math (mirrors the backend's
 * models.FormatPaiseToRupees) and rendering uses Intl with the integer
 * rupee part + a fixed 2-digit paise part — never `paise / 100`.
 *
 * Date helpers re-export the leave-module formatters (one en-IN source
 * of truth across modules).
 *
 * Migration note (2026-06-01, Next.js → Vite): direct 1:1 port from
 * frontend/src/lib/expense-format.ts. No framework deps — pure helpers.
 * Phase 3d (2026-06-01): reinstated the expense-status / batch-status
 * state-machine helpers + status label maps now that the Expense
 * Tracking module is being ported.
 */

import type { BatchStatus, ExpenseStatus } from "@/types/expenses";

export { fmtDate, fmtDateTime, todayISO } from "@/lib/leave-format";

// ---------------------------------------------------------------------------
// Money — integer paise → ₹ rupees (NO float arithmetic anywhere)
// ---------------------------------------------------------------------------

/**
 * Split a (possibly negative) integer paise amount into its rupee and
 * 2-digit paise parts using only integer ops. Mirrors the backend
 * FormatPaiseToRupees split; guards a non-integer / non-finite input by
 * truncating toward zero (defensive — the wire value is always an
 * int64, but a bad cache entry must not throw).
 */
function splitPaise(paise: number): {
  neg: boolean;
  rupees: number;
  frac: number;
} {
  const n = Number.isFinite(paise) ? Math.trunc(paise) : 0;
  const neg = n < 0;
  const abs = Math.abs(n);
  return {
    neg,
    rupees: Math.floor(abs / 100),
    frac: abs % 100,
  };
}

/**
 * Render an INR paise amount as a localised ₹ string, e.g.
 * 123456 → "₹1,23,456.00", -5 → "-₹0.05", 0 → "₹0.00".
 *
 * The integer rupee part is grouped via Intl (en-IN lakh/crore
 * grouping, NO fraction digits so Intl never sees a float); the paise
 * part is a literal 2-digit string appended after the decimal. This
 * keeps every step integer-exact — `paise / 100` is never computed.
 */
export function formatPaise(paise: number): string {
  const { neg, rupees, frac } = splitPaise(paise);
  const grouped = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(rupees);
  const fracStr = frac < 10 ? `0${frac}` : String(frac);
  return `${neg ? "-" : ""}₹${grouped}.${fracStr}`;
}

/**
 * Render an INTEGER paise amount as the editable rupee STRING a money
 * `<Input>` round-trips loss-free through rupeesToPaise() — e.g.
 * 1999 → "19.99", 5000 → "50.00", 0 → "0.00". Always emits a 2-digit
 * fraction (rupeesToPaise pads "5" → "50", so an always-padded form is
 * the unambiguous one) and uses the SAME integer split as formatPaise
 * (splitPaise) so no `paise / 100` float ever appears. This is the one
 * canonical paise→editable-rupee prefill — do not hand-roll the split.
 *
 * Negative inputs (not a valid money cap) collapse to their magnitude;
 * the caller should never pass a negative cap, and rupeesToPaise would
 * reject a leading "-" anyway.
 */
export function paiseToRupeeInput(paise: number): string {
  const { rupees, frac } = splitPaise(paise);
  const fracStr = frac < 10 ? `0${frac}` : String(frac);
  return `${rupees}.${fracStr}`;
}

/**
 * Sum a list of integer paise amounts with integer-exact addition (a
 * running batch total). Non-finite entries are treated as 0 so a bad
 * cache row can't poison the total.
 */
export function sumPaise(amounts: number[]): number {
  let total = 0;
  for (const a of amounts) {
    total += Number.isFinite(a) ? Math.trunc(a) : 0;
  }
  return total;
}

/**
 * Parse a user-typed rupee string ("1234.5", "1,234", "1234.56") into
 * an INTEGER paise amount, WITHOUT float multiplication. We split on
 * the decimal point and combine rupees*100 + paise using integer ops
 * only, so 19.99 → 1999 exactly (a naive `parseFloat * 100` would yield
 * 1998.9999…). Returns null for an empty / malformed / negative input
 * or one with > 2 fraction digits, so the caller can show a field error
 * rather than silently round.
 */
export function rupeesToPaise(input: string): number | null {
  const raw = input.trim().replaceAll(",", "");
  if (raw === "") return null;
  // Optional rupees, optional ".dd" (0, 1 or 2 fraction digits).
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(raw);
  if (!m) return null;
  const rupees = Number(m[1]);
  if (!Number.isSafeInteger(rupees)) return null;
  const fracDigits = m[2] ?? "";
  // Pad "5" → "50" so a single decimal digit means tenths of a rupee.
  const frac = fracDigits === "" ? 0 : Number(fracDigits.padEnd(2, "0"));
  const paise = rupees * 100 + frac;
  return Number.isSafeInteger(paise) && paise > 0 ? paise : null;
}

/** Display name for a user_id given an employee directory lookup
 *  (HR-only directory; non-HR degrades to an id label). */
export function employeeLabel(
  id: number | undefined,
  byId: Map<number, { first_name: string; last_name: string }>,
): string {
  if (id == null) return "—";
  const e = byId.get(id);
  return e ? `${e.first_name} ${e.last_name}` : `User #${id}`;
}

// ---------------------------------------------------------------------------
// Enum → human label maps (no magic strings in the JSX)
// ---------------------------------------------------------------------------

export const EXPENSE_STATUS_LABELS: Record<ExpenseStatus, string> = {
  draft: "Draft",
  submitted: "Submitted",
  approved: "Approved",
  rejected: "Rejected",
  reimbursed: "Reimbursed",
};

export const BATCH_STATUS_LABELS: Record<BatchStatus, string> = {
  open: "Open",
  processing: "Processing",
  paid: "Paid",
  cancelled: "Cancelled",
};

// ---------------------------------------------------------------------------
// Expense state machine (MIRRORS models.CanTransitionExpense /
// CanTransitionBatch — UX gate ONLY; the backend re-checks every move
// atomically in SQL and 409s an illegal one. This never widens what the
// server allows; it only hides a control that *would* 409.)
// ---------------------------------------------------------------------------

/** Owner may submit only a draft (draft → submitted). */
export function canSubmitExpense(status: ExpenseStatus): boolean {
  return status === "draft";
}

/** Owner may delete only a draft (the only "cancel" an expense has). */
export function canDeleteExpense(status: ExpenseStatus): boolean {
  return status === "draft";
}

/** Approver may decide only a submitted expense (→ approved|rejected). */
export function canDecideExpense(status: ExpenseStatus): boolean {
  return status === "submitted";
}

/** Receipts are mutable only while the claim is draft or submitted
 *  (once decided the receipt set is frozen as the audit trail —
 *  mirrors handlers.canModifyReceipts). */
export function canModifyReceipts(status: ExpenseStatus): boolean {
  return status === "draft" || status === "submitted";
}

/** Only an approved, not-yet-batched expense is batch-eligible. */
export function isBatchEligible(e: {
  status: ExpenseStatus;
  reimbursed_batch_id?: number;
}): boolean {
  return e.status === "approved" && e.reimbursed_batch_id == null;
}

/** Legal batch transitions (mirrors models.CanTransitionBatch). */
export function canBatchTransition(
  from: BatchStatus,
  to: BatchStatus,
): boolean {
  switch (from) {
    case "open":
      return to === "processing" || to === "cancelled";
    case "processing":
      return to === "paid" || to === "cancelled";
    default: // paid / cancelled — terminal
      return false;
  }
}
